import {
  Fragment,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'

import { Dialog, Transition } from '@headlessui/react'
import { useAsync } from 'react-use'

import { superFetch } from '~/api-client'
import { config } from '~/config'

import { ApplicationMode, useApplication } from './ApplicationProvider'

export type Session = {
  userId: string
  accessToken: string
  isLoggedIn: boolean
}

const SessionContext = createContext<{
  response?: { data: Session }
  reloadSession: () => Promise<void>
  setNewSession: (userId: string, accessToken: string) => Promise<void>
  openLoginDialog: () => Promise<void>
  logout: () => Promise<void>
  deactivateAccount: () => Promise<void>
}>({
  response: undefined,
  reloadSession: async () => {},
  setNewSession: async () => {},
  openLoginDialog: async () => {},
  logout: async () => {},
  deactivateAccount: async () => {},
})

export const SessionProvider: React.FC<{ children: React.ReactNode }> = (
  props
) => {
  const router = useRouter()
  const [openLoginDialog, setOpenLoginDialog] = useState(false)
  const application = useApplication()

  const [session, setSession] = useState<Session | null>(null)

  const reloadSession = useCallback(async () => {
    setSession(await superFetch<Session>('/api/session'))
  }, [])

  const setNewSession = useCallback(
    async (userId: string, accessToken: string) => {
      const newSession = await superFetch<Session>('/api/session', {
        method: 'POST',
        contentType: 'application/json',
        body: { userId, accessToken },
      })
      setSession(newSession)
    },
    []
  )

  useAsync(async () => {
    reloadSession()
  }, [])

  const logout = useCallback(async () => {
    if (session == null) return

    if (application.mode === 'inapp') {
      application.delegateSignIn()
    } else {
      try {
        await superFetch(config.api.url('/user/signout'), {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${session.accessToken}`,
          },
        })
      } catch (e) {
        // access token 自体の有効期限が切れている場合などは失敗する場合も
        // 後続の処理に進めないといけないため無視する。
        console.error(e)
      }
      await superFetch('/api/session', { method: 'DELETE' })
      setSession(null)
      await router.push('/')
    }
  }, [application, router, session])

  const deactivateAccount = useCallback(async () => {
    if (session == null) return

    await superFetch(config.api.url('/user/deactivate'), {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${session.accessToken}`,
      },
    })

    logout()
  }, [logout, session])

  const requireLogin = useCallback(async () => {
    setOpenLoginDialog(true)
  }, [])

  return (
    <SessionContext.Provider
      value={{
        response: session ? { data: session } : undefined,
        reloadSession,
        setNewSession,
        openLoginDialog: requireLogin,
        logout,
        deactivateAccount,
      }}
    >
      <LoginRequiredDialog
        isOpen={openLoginDialog}
        loginUrl={config.loginUrl.line(router.asPath)}
        onInAppLogin={application.delegateSignIn}
        mode={application.mode}
      />
      {props.children}
    </SessionContext.Provider>
  )
}

const LoginRequiredDialog: React.FC<{
  isOpen: boolean
  loginUrl: string
  onInAppLogin: () => void
  mode: ApplicationMode
}> = (props) => {
  const titleRef = useRef<HTMLHeadingElement>(null)
  const buttonClassName =
    'flex h-10 w-full items-center justify-center rounded px-4 text-center text-[16px] font-bold leading-[1.6] tracking-[0.2px] text-white'
  const isInAppMode =
    props.mode === 'inapp' ||
    (props.mode === 'unsettled' &&
      typeof window !== 'undefined' &&
      window.flutter_inappwebview !== undefined)

  return (
    <Transition appear show={props.isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[999]"
        initialFocus={titleRef}
        onClose={() => {}}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-[#222] bg-opacity-70" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center px-[15px] py-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-[345px] transform overflow-hidden bg-white p-5 text-left transition-all">
                <div className="flex flex-col gap-5">
                  <Dialog.Title
                    ref={titleRef}
                    tabIndex={-1}
                    className="max-w-[296px] text-[13px] font-normal leading-[22px] text-text outline-none"
                  >
                    Foodies Primeは招待制・審査制のグルメコミュニティです。
                  </Dialog.Title>

                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col items-center gap-2">
                      <p className="text-center text-[14px] font-bold leading-[1.6] tracking-[0.2px] text-text">
                        非会員の方はこちら
                      </p>
                      {/* Use a full page navigation so SessionProvider state resets and this dialog closes on the service top page. */}
                      {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
                      <a href="/" className={`${buttonClassName} bg-secondary`}>
                        登録申請する
                      </a>
                    </div>

                    <div className="flex flex-col items-center gap-2">
                      <p className="text-center text-[14px] font-bold leading-[1.6] tracking-[0.2px] text-text">
                        会員の方はこちら
                      </p>
                      {isInAppMode && (
                        <button
                          type="button"
                          onClick={props.onInAppLogin}
                          className={`${buttonClassName} bg-function`}
                        >
                          LINEでログイン
                        </button>
                      )}
                      {!isInAppMode && (
                        <Link
                          href={props.loginUrl}
                          className={`${buttonClassName} bg-function`}
                        >
                          LINEでログイン
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export const useSession = (args?: { loginRequired?: boolean }) => {
  const ctx = useContext(SessionContext)
  useEffect(() => {
    if (args?.loginRequired && ctx.response?.data?.isLoggedIn === false) {
      ctx.openLoginDialog()
    }
  }, [ctx, args])
  return ctx
}

export const useAccessToken = (args?: { loginRequired?: boolean }) => {
  const session = useSession(args)
  return session.response?.data?.accessToken
}
